import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Mail,
  Phone,
  CheckCircle2,
  Clock,
  CircleDot,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const STATUS_META = {
  new: { en: "New", ar: "جديد", color: "bg-sky-50 text-sky-700 border-sky-200", icon: CircleDot },
  in_progress: { en: "In progress", ar: "قيد المعالجة", color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  resolved: { en: "Resolved", ar: "تم الرد", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
};

function StatusPill({ status, lang }) {
  const meta = STATUS_META[status] || STATUS_META.new;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] uppercase tracking-[0.18em] border ${meta.color}`}
    >
      <Icon className="w-3 h-3" />
      {meta[lang] || meta.en}
    </span>
  );
}

function formatDate(iso, isAr) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(isAr ? "ar-KW" : "en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminContacts() {
  const { lang } = useI18n();
  const { user, loading } = useAuth();
  const isAr = lang === "ar";

  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const t = useMemo(
    () => ({
      back: isAr ? "← لوحة التحكم" : "← Dashboard",
      eyebrow: isAr ? "الاستفسارات" : "Inquiries",
      title: isAr ? "كل ما يصلكم من رسائل." : "Every message that arrives.",
      lead: isAr
        ? "الرسائل القادمة عبر نموذج التواصل في الموقع. صنّفها كـ«قيد المعالجة» أو «تم الرد» لمتابعتها."
        : "Messages submitted via the website contact form. Mark them as in-progress or resolved to track follow-ups.",
      filters: {
        all: isAr ? "الكل" : "All",
        new: isAr ? "جديد" : "New",
        in_progress: isAr ? "قيد المعالجة" : "In progress",
        resolved: isAr ? "تم الرد" : "Resolved",
      },
      search: isAr ? "ابحث بالاسم أو البريد…" : "Search by name or email…",
      empty: isAr ? "لا توجد رسائل بعد." : "No inquiries yet.",
      cols: {
        from: isAr ? "المرسل" : "From",
        subject: isAr ? "الموضوع" : "Subject",
        status: isAr ? "الحالة" : "Status",
        received: isAr ? "تاريخ الاستلام" : "Received",
      },
      details: isAr ? "تفاصيل الرسالة" : "Inquiry details",
      message: isAr ? "نص الرسالة" : "Message",
      phone: isAr ? "الهاتف" : "Phone",
      email: isAr ? "البريد" : "Email",
      created: isAr ? "أُرسلت في" : "Submitted",
      close: isAr ? "إغلاق" : "Close",
      mark: isAr ? "تحديث الحالة:" : "Update status:",
      markErr: isAr ? "تعذّر تحديث الحالة." : "Could not update status.",
      markOk: isAr ? "تم التحديث." : "Updated.",
      reply: isAr ? "رد على البريد" : "Reply by email",
      whatsapp: isAr ? "واتساب" : "WhatsApp",
    }),
    [isAr]
  );

  const load = useCallback(() => {
    api
      .get("/admin/contacts")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (loading) return <div className="p-20 text-center text-slate-500 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin/contacts" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const filtered = (items || []).filter((c) => {
    if (filter !== "all" && c.status !== filter) return false;
    if (query) {
      const q = query.toLowerCase();
      return (
        (c.name || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.subject || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  const updateStatus = async (id, newStatus) => {
    setBusy(true);
    try {
      await api.patch(`/admin/contacts/${id}`, { status: newStatus });
      toast.success(t.markOk);
      setSelected((prev) => (prev ? { ...prev, status: newStatus } : prev));
      load();
    } catch {
      toast.error(t.markErr);
    } finally {
      setBusy(false);
    }
  };

  const waLink = (phone) => {
    const clean = (phone || "").replace(/[^0-9]/g, "");
    return clean ? `https://wa.me/${clean}` : null;
  };

  return (
    <div data-testid="admin-contacts-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <Link
        to="/admin"
        data-testid="back-to-dashboard"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 dark:hover:text-amber-500 transition-colors mb-8"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t.back}
      </Link>

      <div className="grid lg:grid-cols-12 gap-12 mb-12">
        <div className="lg:col-span-6">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {t.title}
          </h1>
        </div>
        <div className="lg:col-span-6 lg:pt-3">
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            {t.lead}
          </p>
        </div>
      </div>

      {/* CONTROLS */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {Object.entries(t.filters).map(([k, label]) => (
          <button
            key={k}
            data-testid={`filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-3 h-9 text-xs uppercase tracking-[0.18em] border transition-colors ${
              filter === k
                ? "bg-slate-900 dark:bg-amber-500 text-white dark:text-slate-900 border-slate-900 dark:border-amber-500"
                : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-900 dark:hover:border-amber-500"
            }`}
          >
            {label}
          </button>
        ))}
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.search}
          data-testid="contacts-search"
          dir="auto"
          className="ms-auto h-9 px-3 rounded-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm w-full max-w-xs"
        />
      </div>

      {/* TABLE */}
      <div className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
        {items === null ? (
          <div className="p-10 flex items-center justify-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500 text-sm">{t.empty}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <th className="text-start px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-slate-500 font-normal">{t.cols.from}</th>
                <th className="text-start px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-slate-500 font-normal">{t.cols.subject}</th>
                <th className="text-start px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-slate-500 font-normal">{t.cols.status}</th>
                <th className="text-start px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-slate-500 font-normal">{t.cols.received}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`contact-row-${c.id}`}
                  onClick={() => setSelected(c)}
                  className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer"
                >
                  <td className="px-5 py-4">
                    <p className="font-medium">{c.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{c.email}</p>
                  </td>
                  <td className="px-5 py-4 text-slate-700 dark:text-slate-300 max-w-md truncate">
                    {c.subject || (c.message || "").slice(0, 80) + "…"}
                  </td>
                  <td className="px-5 py-4">
                    <StatusPill status={c.status} lang={lang} />
                  </td>
                  <td className="px-5 py-4 text-xs text-slate-500">
                    {formatDate(c.created_at, isAr)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* DETAILS DRAWER */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-stretch justify-end"
          onClick={() => setSelected(null)}
          data-testid="contact-details-overlay"
        >
          <div
            className="w-full max-w-xl bg-white dark:bg-slate-900 h-full overflow-y-auto border-s border-slate-200 dark:border-slate-800"
            onClick={(e) => e.stopPropagation()}
            data-testid="contact-details-panel"
          >
            <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <p className="eyebrow">{t.details}</p>
              <button
                onClick={() => setSelected(null)}
                data-testid="close-details"
                className="text-slate-400 hover:text-slate-900 dark:hover:text-amber-500 text-sm uppercase tracking-[0.18em]"
              >
                {t.close}
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h2 className="font-display text-3xl">{selected.name}</h2>
                <div className="mt-2 flex items-center gap-3 flex-wrap text-sm">
                  <StatusPill status={selected.status} lang={lang} />
                  <span className="text-slate-500">
                    {formatDate(selected.created_at, isAr)}
                  </span>
                </div>
              </div>

              {/* Quick actions */}
              <div className="flex flex-wrap gap-2">
                <a
                  href={`mailto:${selected.email}?subject=${encodeURIComponent("Re: " + (selected.subject || "Inquiry"))}`}
                  data-testid="action-reply"
                  className="inline-flex items-center gap-2 px-3 h-10 text-xs uppercase tracking-[0.18em] border border-slate-200 dark:border-slate-700 hover:border-slate-900 dark:hover:border-amber-500 transition-colors"
                >
                  <Mail className="w-3.5 h-3.5" /> {t.reply}
                </a>
                {waLink(selected.phone) ? (
                  <a
                    href={waLink(selected.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="action-whatsapp"
                    className="inline-flex items-center gap-2 px-3 h-10 text-xs uppercase tracking-[0.18em] transition-colors"
                    style={{ borderWidth: 1, borderStyle: "solid", borderColor: "var(--wa)", color: "var(--wa)" }}
                  >
                    <Phone className="w-3.5 h-3.5" /> {t.whatsapp}
                  </a>
                ) : null}
              </div>

              {/* Status updater */}
              <div>
                <p className="eyebrow mb-2">{t.mark}</p>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(STATUS_META).map((s) => (
                    <button
                      key={s}
                      type="button"
                      disabled={busy || selected.status === s}
                      onClick={() => updateStatus(selected.id, s)}
                      data-testid={`set-status-${s}`}
                      className={`px-3 h-9 text-xs uppercase tracking-[0.18em] border transition-colors ${
                        selected.status === s
                          ? "bg-slate-900 dark:bg-amber-500 text-white dark:text-slate-900 border-slate-900 dark:border-amber-500"
                          : "border-slate-200 dark:border-slate-700 hover:border-slate-900 dark:hover:border-amber-500"
                      } disabled:opacity-50`}
                    >
                      {STATUS_META[s][lang]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message + meta */}
              <div className="border border-slate-200 dark:border-slate-800 p-5 bg-slate-50 dark:bg-slate-800/30">
                <p className="eyebrow mb-2">{t.message}</p>
                <p className="text-sm whitespace-pre-wrap leading-relaxed text-slate-700 dark:text-slate-300">
                  {selected.message || "—"}
                </p>
              </div>

              <dl className="divide-y divide-slate-100 dark:divide-slate-800 border-y border-slate-200 dark:border-slate-800">
                <Row label={t.email} value={selected.email} />
                <Row label={t.phone} value={selected.phone || "—"} />
                <Row label={t.created} value={formatDate(selected.created_at, isAr)} />
              </dl>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-3">
      <dt className="text-xs uppercase tracking-[0.18em] text-slate-500">{label}</dt>
      <dd className="col-span-2 text-sm text-slate-900 dark:text-slate-100 break-words">{value}</dd>
    </div>
  );
}
